import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Divider,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Calendar,
  Users,
  Building2,
  ClipboardCheck,
  Settings,
  LogOut,
  X,
  Send,
  Ticket,
  User,
  HelpCircle,
  FileText,
} from 'lucide-react';

import { useAppSelector, useAppDispatch } from '@/store';
import { selectCurrentUser, logout } from '@/store/slices/authSlice';
import { logout as apiLogout } from '@/services/api/authService';
import { isAdminOrAbove, isAdminOrManager, normalizeRole } from '@/shared/utils/roles';
import { usePermissions } from '@/shared/contexts/PermissionsContext';
import { PATH_TO_PAGE_KEY, shouldEnforcePagePermissions } from '@/shared/utils/pageAccess';

interface MobileMoreMenuProps {
  open: boolean;
  onClose: () => void;
  onReportIssue?: () => void;
}

/**
 * MobileMoreMenu
 * Slide-up drawer showing additional navigation options on mobile
 */
const MobileMoreMenu: React.FC<MobileMoreMenuProps> = ({ open, onClose, onReportIssue }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);
  const { canAccessPage, isPermissionsReady } = usePermissions();

  const isManager = isAdminOrManager(user?.role);
  const isAdmin = isAdminOrAbove(user?.role);
  const canViewSettings = isAdmin;
  const effectiveRole = normalizeRole(user?.role);
  const enforcePages = shouldEnforcePagePermissions(effectiveRole);

  const canSeePath = (path: string, roleAllowed: boolean) => {
    if (!roleAllowed) return false;
    if (isAdmin) return true;
    if (!enforcePages) return true;
    const pageKey = PATH_TO_PAGE_KEY[path];
    if (!pageKey) return true;
    if (!isPermissionsReady) return false;
    return canAccessPage(pageKey);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleLogout = async () => {
    onClose();
    await apiLogout();
    dispatch(logout());
    navigate('/login');
  };

  const isActivePath = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const menuItems = [
    { path: '/tickets', label: 'Tickets', icon: Ticket, show: canSeePath('/tickets', true) },
    { path: '/calendar', label: 'Calendar', icon: Calendar, show: canSeePath('/calendar', true) },
    { path: '/documents', label: 'Documents', icon: FileText, show: canSeePath('/documents', true) },
    { path: '/delegated', label: 'Delegated Tasks', icon: Send, show: canSeePath('/delegated', true) },
    { path: '/team', label: 'Team', icon: Users, show: canSeePath('/team', isManager) },
    { path: '/departments', label: 'Departments', icon: Building2, show: canSeePath('/departments', isManager) },
    { path: '/reviews', label: 'Reviews & Approval', icon: ClipboardCheck, show: canSeePath('/reviews', isManager) },
    { path: '/users', label: 'Users', icon: User, show: canSeePath('/users', isAdmin) },
  ];

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '80vh',
          pb: 'env(safe-area-inset-bottom)',
        },
      }}
    >
      {/* Handle bar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          pt: 1.5,
          pb: 1,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 4,
            bgcolor: 'grey.300',
            borderRadius: 2,
          }}
        />
      </Box>

      {/* Header with close button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          pb: 2,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          More Options
        </Typography>
        <IconButton onClick={onClose} size="small">
          <X size={20} />
        </IconButton>
      </Box>

      {/* User Profile Card */}
      <Box
        sx={{
          mx: 2,
          mb: 2,
          p: 2,
          bgcolor: 'grey.50',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
        onClick={canViewSettings ? () => handleNavigation('/settings') : undefined}
      >
        <Avatar
          sx={{
            width: 48,
            height: 48,
            bgcolor: 'primary.main',
            fontSize: '1.25rem',
            fontWeight: 600,
          }}
        >
          {user?.firstName?.[0]?.toUpperCase() || 'U'}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
          <Typography variant="subtitle1" fontWeight={600} noWrap title={user?.displayName || 'User'}>
            {user?.displayName || 'User'}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap title={user?.email} sx={{ display: 'block' }}>
            {user?.email}
          </Typography>
        </Box>
        <Chip
          label={user?.role?.replace('_', ' ') || 'User'}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ textTransform: 'capitalize', fontSize: '0.65rem' }}
        />
      </Box>

      <Divider />

      {/* Navigation Items */}
      <List sx={{ px: 1, py: 1 }}>
        {menuItems
          .filter((item) => item.show)
          .map((item) => {
            const Icon = item.icon;
            const isActive = isActivePath(item.path);
            return (
              <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    borderRadius: 2,
                    minHeight: 52,
                    bgcolor: isActive ? 'primary.50' : 'transparent',
                    color: isActive ? 'primary.main' : 'text.primary',
                    '&:active': {
                      bgcolor: isActive ? 'primary.100' : 'grey.100',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 44,
                      color: 'inherit',
                    }}
                  >
                    <Icon size={22} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '0.95rem',
                      fontWeight: isActive ? 600 : 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
      </List>
      <Divider />

      {/* Settings & Help */}
      <List sx={{ px: 1, py: 1 }}>
        {canViewSettings && (
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => handleNavigation('/settings')}
              sx={{
                borderRadius: 2,
                minHeight: 52,
                bgcolor: isActivePath('/settings') ? 'primary.50' : 'transparent',
                color: isActivePath('/settings') ? 'primary.main' : 'text.primary',
              }}
            >
              <ListItemIcon sx={{ minWidth: 44 }}>
                <Settings size={22} />
              </ListItemIcon>
              <ListItemText
                primary="Settings"
                primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
        )}

        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            sx={{ borderRadius: 2, minHeight: 52 }}
            onClick={() => {
              onClose();
              onReportIssue?.();
            }}
          >
            <ListItemIcon sx={{ minWidth: 44, color: 'text.secondary' }}>
              <HelpCircle size={22} />
            </ListItemIcon>
            <ListItemText
              primary="Report an issue"
              primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }}
            />
          </ListItemButton>
        </ListItem>
      </List>

      <Divider />

      {/* Logout */}
      <List sx={{ px: 1, py: 1 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              minHeight: 52,
              color: 'error.main',
              '&:active': {
                bgcolor: 'error.50',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 44, color: 'inherit' }}>
              <LogOut size={22} />
            </ListItemIcon>
            <ListItemText
              primary="Sign Out"
              primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }}
            />
          </ListItemButton>
        </ListItem>
      </List>

      {/* Bottom spacing for safe area */}
      <Box sx={{ height: 8 }} />
    </Drawer>
  );
};

export default MobileMoreMenu;
